/**
 * The web app's stored sign-in: the tokens the custom login form obtained
 * through the backend's Keycloak password grant, kept in localStorage and
 * handed to keycloak-js on every page load.
 *
 * A password grant creates a Keycloak session but never a browser SSO
 * cookie, so the Keycloak-hosted login page can never sign this session in
 * silently. Everything here exists to keep the app from ever bouncing a user
 * to that page for a normal login: decide up front whether the stored refresh
 * token can still work, and scrub the leftovers of a failed OIDC redirect so
 * keycloak-js does not mistake them for a fresh callback and ignore the tokens
 * it was given.
 */

export const ACCESS_TOKEN_KEY = 'edgelex_access_token';
export const REFRESH_TOKEN_KEY = 'edgelex_refresh_token';
export const FIRM_ID_KEY = 'edgelexFirmId';

/** keycloak-js remembers each redirect it starts under this prefix. */
const KEYCLOAK_CALLBACK_PREFIX = 'kc-callback-';

/**
 * Refresh at least this often while the app is open. The realm's SSO session
 * idle timeout is 30 minutes: a session nothing has touched for that long is
 * gone, whatever the access token's own lifetime says.
 */
export const SESSION_KEEPALIVE_INTERVAL_MS = 20 * 60 * 1000;

/** Treat a token that expires within this many seconds as already expired. */
export const TOKEN_EXPIRY_SKEW_SECONDS = 30;

export interface StoredWebSession {
  firmId: string;
  accessToken: string;
  refreshToken: string;
}

const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
};

/** The `exp` claim of a JWT in seconds since the epoch, or null when unreadable. */
export function decodeJwtExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { exp?: unknown };
    return typeof payload.exp === 'number' && Number.isFinite(payload.exp) ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when the refresh token is present, readable and not about to expire. */
export function isRefreshTokenUsable(
  refreshToken: string | null | undefined,
  nowMs = Date.now(),
  skewSeconds = TOKEN_EXPIRY_SKEW_SECONDS,
): boolean {
  const exp = decodeJwtExpiry(refreshToken);
  if (exp == null) return false;
  return exp - skewSeconds > nowMs / 1000;
}

export function readStoredWebSession(storage: Storage = localStorage): StoredWebSession | null {
  const firmId = storage.getItem(FIRM_ID_KEY);
  const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = storage.getItem(REFRESH_TOKEN_KEY);
  if (!firmId || !accessToken || !refreshToken) return null;
  return { firmId, accessToken, refreshToken };
}

/** Remove the stored sign-in and any redirect bookkeeping keycloak-js left behind. */
export function clearStoredWebSession(storage: Storage = localStorage): void {
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
  storage.removeItem(FIRM_ID_KEY);
  removeKeycloakCallbackEntries(storage);
}

function removeKeycloakCallbackEntries(storage: Storage, state?: string): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && key.startsWith(KEYCLOAK_CALLBACK_PREFIX) && (!state || key === KEYCLOAK_CALLBACK_PREFIX + state)) {
      keys.push(key);
    }
  }
  keys.forEach((key) => storage.removeItem(key));
}

const parseCallbackParams = (fragmentOrQuery: string): URLSearchParams =>
  new URLSearchParams(fragmentOrQuery.replace(/^[#?]/, ''));

/**
 * A Keycloak redirect that came back with an error (for example
 * `#error=login_required&state=…` after a silent check with no SSO cookie)
 * carries nothing the app can use. Left in the URL, it survives the next
 * reload and keycloak-js treats it as the current callback: it reports
 * "not authenticated" without ever trying the tokens it was given. Strip it
 * from the address bar and forget its state entry. A callback carrying an
 * authorization `code` is a real sign-in in progress and is left alone.
 *
 * Returns true when something was removed.
 */
export function stripFailedOidcCallback(
  win: Pick<Window, 'location' | 'history'> = window,
  storage: Storage = localStorage,
): boolean {
  const { location, history } = win;
  let stripped = false;
  for (const raw of [location.hash, location.search]) {
    if (!raw || raw.length < 2) continue;
    const params = parseCallbackParams(raw);
    if (!params.has('error') || params.has('code')) continue;
    const state = params.get('state');
    if (state) removeKeycloakCallbackEntries(storage, state);
    stripped = true;
  }
  if (stripped) {
    history.replaceState(history.state, '', location.pathname);
  }
  return stripped;
}

/** The address to land on after the custom form signs in: same page, no callback leftovers. */
export function postLoginUrl(location: Pick<Location, 'origin' | 'pathname' | 'search'> = window.location): string {
  const params = new URLSearchParams(location.search);
  ['error', 'error_description', 'error_uri', 'state', 'session_state', 'code', 'iss', 'reset'].forEach((key) => params.delete(key));
  const search = params.toString();
  return `${location.origin}${location.pathname}${search ? `?${search}` : ''}`;
}
